import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { ClientProfileResponse, useGetClientProfile } from '@/api/clients/get-client-profile'
import { useGetClientProfileSettings } from '@/api/clients/get-client-profile-settings'
import { useUpdateClientProfile } from '@/api/clients/update-client-profile'
import { useGetMailingList } from '@/api/communication/get-mailing-list'
import { useGetClientMailingLists } from '@/api/mailing-list/get-client-mailing-lists'
import { useUpdateMailingListSubscription } from '@/api/mailing-list/update-mailing-list-subscription'
import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { TabCard } from '@/components/layout/tab-card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils/error'
import { stripPhoneCountryCode } from '@/utils/phone-number'

import {
  canEditGeneralInfo,
  composeUpdateFormData,
  makeGeneralInfoSchema,
} from './general-information.constants'
import { GeneralInfoFormValues, GeneralInformationProps } from './general-information.types'
import { computeDirtyFieldChanges, getPmsIdInfo } from './general-information.utils'
import { GeneralInformationEditFields } from './general-information-edit-fields'
import { GeneralInformationSkeleton } from './general-information-skeleton'
import { GeneralInformationViewFields } from './general-information-view-fields'

const composeFormValues = (
  data: ClientProfileResponse,
  mailingListIds: string[],
): GeneralInfoFormValues => ({
  address: data.street ?? '',
  buildingId: data.buildingId.id,
  dateOfBirth: data.birth ? new Date(data.birth) : null,
  firstName: data.user.firstName,
  floor: data.floor ? String(data.floor) : '',
  inHouseDelivery: data.inHouse ?? false,
  lastName: data.user.lastName,
  leaseEndDate: data.leaseEnd ? new Date(data.leaseEnd) : null,
  leaseStartDate: data.leaseStart ? new Date(data.leaseStart) : null,
  mailingList: mailingListIds,
  moveInDate: data.moveInDate ? new Date(data.moveInDate) : null,
  moveOutDate: data.moveOutDate ? new Date(data.moveOutDate) : null,
  pets: data.pets ?? false,
  phoneNumber: stripPhoneCountryCode(data.phone ? String(data.phone) : ''),
  photo: null,
  removeExistingPhoto: false,
  tags: data.tags ?? [],
  unit: data.apartment ? String(data.apartment) : '',
  zipCode: data.zipCode ?? '',
})

export const GeneralInformation = ({ clientId, onDirtyChange }: GeneralInformationProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [isEditing, setIsEditing] = useState(false)
  const userRole = useSelector(selectUserRole)
  const allBuildings = useSelector(selectBuildingsForFilter)
  const buildingLabelMapRef = useRef<Record<string, string>>({})
  buildingLabelMapRef.current = Object.fromEntries(allBuildings.map((b) => [String(b.id), b.text]))

  const { data: clientData, isLoading: isClientLoading } = useGetClientProfile({ clientId })
  // Note: PMS settings must key off the resident's own building, not the
  // globally selected one — switching the top building selector while a
  // profile is open must not re-apply another building's PMS config to it.
  const buildingId = clientData?.buildingId.id
  const { data: settings, isLoading: isSettingsLoading } = useGetClientProfileSettings({
    buildingId,
  })
  const { data: clientMailingLists = [], isLoading: isMailingListLoading } =
    useGetClientMailingLists({ clientId })
  // Door-to-Door is only truly active when it's both allowed at the Admin level
  // (buildingId.packagesD2DAvailable) and turned on for this building's packages (d2dMode) - see
  // GeneralTab, where packagesD2DAvailable gates whether d2dMode can even be toggled. In House
  // Delivery is the fallback shown whenever Door-to-Door isn't effectively available.
  const { data: packagesSettings, isLoading: isPackagesSettingsLoading } = useGetPackagesSettings({
    buildingId: buildingId ?? 0,
  })

  const hiddenFields = settings?.hiddenFields ?? []
  const syncedFields = settings?.syncedFields ?? []

  const { mutateAsync, isPending } = useUpdateClientProfile(clientId)
  const { mutateAsync: mutateSubscription, isPending: isSubscriptionPending } =
    useUpdateMailingListSubscription(clientId)

  const mailingListIds = clientMailingLists.map((item) => String(item.id))

  const { data: allMailingLists = [] } = useGetMailingList(
    { buildingId: buildingId ?? 0 },
    { enabled: !!buildingId },
  )
  const mailingListLabelMapRef = useRef<Record<string, string>>({})
  mailingListLabelMapRef.current = Object.fromEntries(
    allMailingLists.map((item) => [String(item.id), item.value]),
  )

  const generalInfoSchema = useMemo(() => makeGeneralInfoSchema(t, syncedFields), [t, syncedFields])

  const form = useForm<GeneralInfoFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(generalInfoSchema),
    values: clientData ? composeFormValues(clientData, mailingListIds) : undefined,
  })

  const getChanges = useCallback(
    () =>
      computeDirtyFieldChanges(
        form.formState.dirtyFields as Record<string, boolean | undefined>,
        (form.formState.defaultValues ?? {}) as Record<string, unknown>,
        form.getValues() as Record<string, unknown>,
        {
          buildingId: (id) => buildingLabelMapRef.current[id] ?? id,
          mailingList: (id) => mailingListLabelMapRef.current[id] ?? id,
        },
      ),
    [],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && form.formState.isDirty
    onDirtyChange?.(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, form.formState.isDirty])

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  // Note: read during render so RHF tracks dirtyFields
  const { dirtyFields } = form.formState

  const onSubmit = async (values: GeneralInfoFormValues) => {
    const isMailingListDirty = !!dirtyFields.mailingList

    try {
      const formData = composeUpdateFormData(values, syncedFields, !!dirtyFields.floor)
      await mutateAsync({ data: formData })
      toastFactory.success(t('clients:generalInformation.toasts.updateSuccess'))
      setIsEditing(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
      return
    }

    if (!isMailingListDirty) return

    try {
      await mutateSubscription({ subscribe: values.mailingList.map(Number) })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSave = useFormSave({ form, onSubmit, onUnchanged: () => setIsEditing(false) })

  if (isClientLoading || isSettingsLoading || isMailingListLoading || isPackagesSettingsLoading)
    return <GeneralInformationSkeleton />
  if (!clientData) return null

  const isDoorToDoorEffective =
    (clientData.buildingId.packagesD2DAvailable ?? false) && (packagesSettings?.d2dMode ?? false)

  const pmsIdInfo = getPmsIdInfo(clientData)

  const sharedFieldProps = {
    buildingId,
    clientData,
    clientMailingLists,
    hiddenFields,
    pmsIdInfo,
    showInHouseDelivery: !isDoorToDoorEffective,
    syncedFields,
    userRole,
  }

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          isEditing ? (
            <>
              <Button size="small" use="secondary" onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                size="small"
                loading={isPending || isSubscriptionPending}
                onClick={handleSave}
              >
                {t('common:actions.saveChanges')}
                <Icon icon={CheckIcon} />
              </Button>
            </>
          ) : (
            canEditGeneralInfo(userRole) && (
              <Button size="small" onClick={() => setIsEditing(true)}>
                {t('common:actions.edit')}
                <Icon icon={PencilSimpleIcon} />
              </Button>
            )
          )
        }
        title={t('clients:generalInformation.title')}
      >
        {isEditing ? (
          <GeneralInformationEditFields {...sharedFieldProps} />
        ) : (
          <GeneralInformationViewFields {...sharedFieldProps} />
        )}
      </TabCard>
    </FormProvider>
  )
}
